/**
 * StepperControl — Reusable ± button group with center value display.
 * 48×48 touch targets, press feedback with scale + teal highlight.
 */

import { useState } from 'react';
import { T } from '@/styles/tokens';

interface StepperControlProps {
  value: string;
  onDecrement: () => void;
  onIncrement: () => void;
  width?: number;
}

function StepButton({ dir, onPress }: { dir: 'minus' | 'plus'; onPress: () => void }) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onPointerDown={() => { setPressed(true); onPress(); }}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        width: 48,
        height: 48,
        borderRadius: 14,
        border: `1.5px solid ${pressed ? T.primary : T.charcoal200}`,
        background: pressed ? T.tealSoft : T.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: pressed ? T.primary : T.charcoal400,
        transform: pressed ? 'scale(0.94)' : 'scale(1)',
        transition: 'all 100ms ease',
        touchAction: 'manipulation',
        padding: 0,
        flexShrink: 0,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        {dir === 'minus' ? (
          <path d="M3.5 8h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        ) : (
          <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        )}
      </svg>
    </button>
  );
}

export function StepperControl({ value, onDecrement, onIncrement, width = 80 }: StepperControlProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
      <StepButton dir="minus" onPress={onDecrement} />
      <div style={{
        width,
        textAlign: 'center',
        fontSize: 26,
        fontWeight: 800,
        color: T.text,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: T.fontFamily,
      }}>
        {value}
      </div>
      <StepButton dir="plus" onPress={onIncrement} />
    </div>
  );
}
